import { useEffect, useRef } from "react";
import { toast } from "sonner";
import { CheckCircle2, AlertTriangle, Clock, XCircle } from "lucide-react";
import { useSubscription } from "@/hooks/useSubscription";

/**
 * Listens for realtime subscription transitions (driven by the Stripe webhook
 * upserting into `subscriptions`) and surfaces a sonner toast the moment the
 * user's Pro state actually changes. Mount once near the app root.
 *
 * Transitions handled:
 *  - free → pro          → "Pro activated" success toast
 *  - pro  → free         → "Pro access ended" warning toast
 *  - active → past_due   → dunning warning toast
 *  - active → canceling  → "Cancellation scheduled" info toast
 *  - canceling → active  → "Cancellation reverted" success toast
 */
export function SubscriptionStatusToaster() {
  const { loading, isPro, isPastDue, isCanceling, subscription } = useSubscription();

  // Snapshot of the previous derived state. Initialized to `null` so we can
  // distinguish "first load" from a real transition and avoid a phantom toast
  // on page refresh.
  const prev = useRef<{ isPro: boolean; isPastDue: boolean; isCanceling: boolean } | null>(null);

  useEffect(() => {
    if (loading) return;
    const next = { isPro, isPastDue, isCanceling };

    // First settled read — just record it.
    if (prev.current === null) {
      prev.current = next;
      return;
    }

    const before = prev.current;

    // Pro gained
    if (!before.isPro && next.isPro) {
      toast.success("Pro activated", {
        id: "sub-pro-activated",
        description: subscription?.status === "trialing"
          ? "Your free trial has started — unlimited pieces unlocked."
          : "Your subscription is live — unlimited pieces unlocked.",
        icon: <CheckCircle2 className="h-4 w-4" />,
        duration: 6000,
      });
    }

    // Pro lost
    if (before.isPro && !next.isPro) {
      toast.warning("Pro access ended", {
        id: "sub-pro-ended",
        description: "You're back on the Free plan. Upgrade any time from Pricing.",
        icon: <XCircle className="h-4 w-4" />,
        duration: 7000,
      });
    }

    // Entered past_due (renewal failed, Stripe retrying)
    if (!before.isPastDue && next.isPastDue) {
      toast.warning("Payment retry in progress", {
        id: "sub-past-due",
        description: "Your last renewal failed. We'll retry automatically — update your card to avoid losing access.",
        icon: <AlertTriangle className="h-4 w-4" />,
        duration: 8000,
      });
    }

    // Cancellation scheduled at period end
    if (!before.isCanceling && next.isCanceling) {
      toast("Cancellation scheduled", {
        id: "sub-canceling",
        description: subscription?.current_period_end
          ? `Pro stays active until ${new Date(subscription.current_period_end).toLocaleDateString()}.`
          : "Pro stays active until the end of your current period.",
        icon: <Clock className="h-4 w-4" />,
        duration: 6000,
      });
    }

    // Cancellation reverted while still active
    if (before.isCanceling && !next.isCanceling && next.isPro) {
      toast.success("Cancellation reverted", {
        id: "sub-resumed",
        description: "Your subscription will renew as usual.",
        icon: <CheckCircle2 className="h-4 w-4" />,
        duration: 5000,
      });
    }

    prev.current = next;
  }, [loading, isPro, isPastDue, isCanceling, subscription?.status, subscription?.current_period_end]);

  return null;
}
